/**
 * The Venues page (BON-538 phase 2).
 *
 * One roster and one map over one request. The region filter is the QUERY
 * STRING — `?region=<slug>` — so the scope survives a reload, is linkable, and
 * drives both panels from a single piece of state; their counts cannot drift
 * apart because they are reading the same array.
 *
 * Search and sort are held here rather than in the roster card for the same
 * reason: narrowing the list narrows the map with it, so the page always shows
 * one answer rather than two.
 *
 * Replaces `RegionalVenues` and `AdminVenues`, which were two persona copies of
 * this page reading a warehouse-backed endpoint.
 */
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { DashboardsPage } from '../../DashboardsPage';
import { useRegionHierarchy } from '../../primitives/scope/region-hierarchy';
import { Spinner } from '../../primitives/Spinner';
import { useTableSort } from '../../primitives/useTableSort';
import VenueCreateDrawer from '../VenueCreateDrawer';

import { buildRegionColors } from './region-colors';
import { RegionColorsContext } from './region-colors-context';
import { useVenuesRosterView } from './use-venues-roster-view';
import { VENUE_SORTS } from './venue-columns';
import { venueHref } from './venue-routes';
import { RegionNotFoundError, type VenueRosterRow } from './venues-roster-api';
import { useVenuesLayout } from './venues-viewport';
import { VenuesAddButton } from './VenuesAddButton';
import { VenuesBody } from './VenuesBody';
import { VenuesHeader } from './VenuesHeader';

const NOTE: React.CSSProperties = {
  padding: 14,
  borderRadius: 10,
  background: 'var(--nb-bg)',
  border: '1px dashed var(--nb-border)',
  fontSize: 12.5,
  color: 'var(--nb-subtle)',
};

/** A mistyped region and a broken request say different things. */
const failureMessage = (error: unknown): string =>
  error instanceof RegionNotFoundError
    ? `${error.message}. Pick a region from the list above.`
    : 'Venues could not be loaded. Try again in a moment.';

export function VenuesPage() {
  const navigate = useNavigate();
  const layout = useVenuesLayout();

  const [search, setSearch] = useState('');
  const [adding, setAdding] = useState(false);
  const sort = useTableSort<VenueRosterRow>(VENUE_SORTS);
  const view = useVenuesRosterView();
  const { data, isLoading, isError, error, regionScope, tasksOnly, scope } = view;

  // The roster row knows its region and its parent, but not where that region
  // RANKS among its siblings — which is what decides its colour. The hierarchy
  // query already loads for the scope picker, so this costs nothing extra.
  const { data: regions } = useRegionHierarchy();
  const colors = useMemo(() => buildRegionColors(regions ?? []), [regions]);

  // Filtered once, here, so the roster and the map read the same array.
  const venues = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const rows = data?.venues ?? [];
    return sort.sorted(needle ? rows.filter((v) => v.name.toLowerCase().includes(needle)) : rows);
  }, [data, search, sort]);

  const header = (
    <VenuesHeader
      label={scope.label}
      currentRegionId={scope.currentRegionId}
      isOwnRegions={view.isOwnRegions}
      noRegions={regionScope.noRegions}
      isPhone={layout === 'phone'}
      onAddVenue={() => setAdding(true)}
    />
  );

  if (isError) {
    return (
      <DashboardsPage header={header}>
        <p style={NOTE}>{failureMessage(error)}</p>
      </DashboardsPage>
    );
  }

  if (regionScope.pending || isLoading || !data) {
    return (
      <DashboardsPage header={header}>
        <div style={{ ...NOTE, display: 'flex', alignItems: 'center', gap: 10 }}>
          <Spinner />
          Loading venues…
        </div>
      </DashboardsPage>
    );
  }

  return (
    <DashboardsPage header={header} stack={false} hasPhoneBottomBar={layout === 'phone'}>
      <RegionColorsContext.Provider value={colors}>
        <VenuesBody
          venues={venues}
          search={search}
          onSearchChange={setSearch}
          sort={sort}
          layout={layout}
          tasksOnly={tasksOnly}
          onTasksOnlyChange={view.setTasksOnly}
          regionScoped={regionScope.regions.length > 0}
          fitKey={regionScope.regions.join(',') || 'all'}
        />
      </RegionColorsContext.Provider>
      {layout === 'phone' && <VenuesAddButton onClick={() => setAdding(true)} />}
      {adding && (
        <VenueCreateDrawer
          regionLabel={data.scope?.region.name}
          onClose={() => setAdding(false)}
          onCreated={(venue) => {
            setAdding(false);
            navigate(venueHref(venue));
          }}
        />
      )}
    </DashboardsPage>
  );
}
